// ─────────────────────────────────────────────────────────────────────────────
// Comp visibility scope — the SINGLE authority on which competitions a given
// session is allowed to SEE. Pure: no React, no storage, no I/O.
//
// WHY THIS EXISTS (2026-08-02)
// The rule used to be one expression inlined in workspace.ts:
//
//     const orgScoped = entryOrg !== null && !isFounder;
//     ... orgScoped && scoped.length === 0 ? comps : scoped
//
// It leaked, twice over, and both leaks are the same mistake: the scope was
// allowed to WIDEN when it could not narrow.
//
//   1. `entryOrg !== null` was a PRECONDITION for scoping at all. Handshake.tsx
//      deletes the entry org whenever the redeem carries no org context — the
//      founder's normal /ggportal login. With entryOrg null the whole
//      conjunction was false, so a view-as preview got `comps` unfiltered: every
//      customer org's competition names and statuses, rendered inside what the
//      founder believed was a member's view.
//   2. `scoped.length === 0 ? comps : scoped` — "rather than a dead UI". An
//      empty narrowed slice fell all the way back to the platform-wide list. A
//      scope that fails OPEN when it narrows to nothing is not a scope, and for
//      a FOUNDER the fallback is literally every customer's data.
//
// THE RULE THIS MODULE ENFORCES, structurally rather than by remembering:
// while a preview is active, `all` is not a reachable outcome. The resolver
// cannot return it, so no consumer can render it, and no future edit to the
// filter can reintroduce the fallback — there is nothing to fall back TO.
// Under a preview the scope is either a specific org or NOTHING.
//
// Fail-direction, recorded per architectural law: FAIL CLOSED. Confidentiality
// scoping shows too little rather than too much. An empty comp picker during a
// preview is the honest render of a persona holding no org grant — the same
// call viewAs.ts already made for discordId ("pages that fetch by the caller's
// own id show empty/not-found under a preview... far better than a preview
// quietly confirming the founder's own access").
// ─────────────────────────────────────────────────────────────────────────────

/** What a session may see. `all` is UNREACHABLE while a preview is active. */
export type CompScope =
  /** No narrowing — the caller's own server-scoped list, as fetched. */
  | { kind: 'all' }
  /** Exactly one org's slice. */
  | { kind: 'org'; orgId: string }
  /** No derivable scope. Renders nothing. NEVER widens to `all`. */
  | { kind: 'none' };

export interface CompScopeInput {
  /** Is a founder view-as preview active right now? */
  previewActive: boolean;
  /** The org the preview explicitly simulates (view-as org admin/owner). */
  previewOrgId: string | null;
  /** The org this browser session entered through (Handshake/T07). Often null
   *  — a founder logging in via /ggportal carries no org context at all. */
  entryOrgId: string | null;
  /** The EFFECTIVE (view-as-adjusted) founder flag. */
  isFounder: boolean;
}

/**
 * Org ids reach this module in two shapes — bare slugs (`alpha`, from the
 * handshake and the view-as session) and prefixed keys (`ORG#alpha`, from DDB
 * rows on Competition.orgId). Normalising both sides is what makes the
 * comparison an equality rather than a guess.
 *
 * Note this can only ever make the filter MATCH MORE PRECISELY, never match a
 * different org: `ORG#` is a fixed key prefix in this estate, not part of any
 * org's identity.
 */
function normalizeOrgId(orgId: string): string {
  return orgId.startsWith('ORG#') ? orgId.slice('ORG#'.length) : orgId;
}

/**
 * Resolve what this session may see.
 *
 * Order matters and is deliberate:
 *   • A preview is decided FIRST and in isolation, so no real-session fact
 *     (entryOrg's absence, the founder's real role) can leak `all` into it.
 *   • An explicitly simulated org beats the entry org — it is the scope the
 *     founder actually asked to preview.
 *   • The entry org is still honoured under a preview because it can only
 *     NARROW. It is never the thing that ENABLES scoping.
 */
export function resolveCompScope(input: CompScopeInput): CompScope {
  const { previewActive, previewOrgId, entryOrgId, isFounder } = input;

  if (previewActive) {
    if (previewOrgId) return { kind: 'org', orgId: previewOrgId };
    if (entryOrgId) return { kind: 'org', orgId: entryOrgId };
    // A persona with no org grant and no entry context. There is no honest
    // list to show it, and the platform-wide list is emphatically not one.
    return { kind: 'none' };
  }

  // ── Real session, no preview ───────────────────────────────────────────────
  // A real non-founder's `comps` came back from the API already scoped to what
  // they may see; the entry org is a further, cosmetic narrowing (T07). With no
  // entry org there is nothing to narrow BY, and `all` here means "their own
  // list", not "the platform's" — so this branch is not the leak the preview
  // branch above is, and must stay as it is or every direct-login member gets
  // an empty portal.
  if (entryOrgId && !isFounder) return { kind: 'org', orgId: entryOrgId };
  return { kind: 'all' };
}

/**
 * Apply a scope to a comp list.
 *
 * There is NO empty-result fallback here, and that absence is the fix. If a
 * scope narrows to nothing, the answer is nothing.
 */
export function filterCompsToScope<T extends { orgId?: string | null }>(
  comps: T[],
  scope: CompScope,
): T[] {
  if (scope.kind === 'all') return comps;
  if (scope.kind === 'none') return [];
  const target = normalizeOrgId(scope.orgId);
  return comps.filter(c => !!c.orgId && normalizeOrgId(c.orgId) === target);
}
